import React, { useCallback, useEffect, useId, useState } from 'react';
import toast from 'react-hot-toast';
import {
  CirclePlay, CreditCard, Hourglass, Info, Pencil, UserPlus,
} from 'lucide-react';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import LoadingState from '../../components/LoadingState/LoadingState';
import { getReminderRules, updateReminderRule } from '../../api/reminders';
import { useI18n } from '../../i18n/I18nContext';
import { errorText } from '../../utils/errorText';
import { formatNumber } from '../../utils/datetime';
import { ChannelIcons } from './reminderView';
import RuleEditModal from './RuleEditModal';
import { conversionKind, ruleMeta, sortRules } from './reminderUi';

// One icon per situation the rules answer.
const RULE_ICONS = {
  NO_COURSE: UserPlus,
  UNPAID: CreditCard,
  NOT_STARTED: CirclePlay,
  INACTIVE: Hourglass,
};

/**
 * "Avtomatik": the nine reminders the server sends on its own (on by default), each with the
 * situation it answers, its timing, a switch, what it achieved in 30 days and an Edit button.
 * The caps are stated above the list: once per rule per person, at most 1 a day and 3 a week,
 * only between 09:00 and 21:00 Tashkent time.
 */
export default function RulesTab() {
  const { t } = useI18n();
  const uid = useId();
  const [state, setState] = useState({ phase: 'loading', rules: [], error: null });
  const [busy, setBusy] = useState(() => new Set());
  const [editing, setEditing] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ phase: 'loading', rules: prev.rules, error: null }));
    getReminderRules()
      .then((rules) => { if (!cancelled) setState({ phase: 'ready', rules: sortRules(rules), error: null }); })
      .catch((error) => { if (!cancelled) setState({ phase: 'error', rules: [], error }); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const replace = useCallback((saved) => {
    setState((prev) => ({ ...prev, rules: prev.rules.map((rule) => (rule.key === saved.key ? { ...rule, ...saved } : rule)) }));
  }, []);

  const toggle = async (rule) => {
    const enabled = !rule.enabled;
    replace({ key: rule.key, enabled });
    setBusy((prev) => new Set(prev).add(rule.key));
    try {
      const saved = await updateReminderRule(rule.key, { enabled });
      if (saved && saved.key === rule.key) replace(saved);
    } catch (err) {
      replace({ key: rule.key, enabled: rule.enabled });
      toast.error(errorText(err, t('reminders.rules.saveError')));
    } finally {
      setBusy((prev) => {
        const next = new Set(prev);
        next.delete(rule.key);
        return next;
      });
    }
  };

  const { phase, rules, error } = state;

  if (phase === 'loading' && rules.length === 0) return <LoadingState skeleton="list" rows={6} message={t('common.loading')} />;
  if (phase === 'error') {
    return error?.code === 403 ? (
      <PageEmptyState variant="forbidden" size="sm" />
    ) : (
      <PageEmptyState
        variant="error"
        size="sm"
        title={t('reminders.rules.loadError')}
        onRetry={() => setReloadKey((k) => k + 1)}
      />
    );
  }

  return (
    <div className="ui-stack">
      <div className="ui-alert" data-tone="info">
        <Info aria-hidden="true" />
        <p>{t('reminders.rules.caps')}</p>
      </div>

      <section className="section-card rem-rules" aria-labelledby={`${uid}-title`}>
        <div className="section-head">
          <div>
            <h2 className="section-head__title" id={`${uid}-title`}>{t('reminders.rules.title')}</h2>
            <p className="section-head__desc">{t('reminders.rules.subtitle')}</p>
          </div>
        </div>
        <ul className="rem-rule-list">
          {rules.map((rule) => {
            const meta = ruleMeta(rule.key);
            const known = meta.audience !== null;
            const title = known ? t(`reminders.rules.items.${rule.key}.title`) : rule.key;
            const stats = rule.stats30d || {};
            const audience = rule.audience || meta.audience;
            const kind = conversionKind(audience);
            const Icon = RULE_ICONS[audience] || UserPlus;
            return (
              <li key={rule.key} className={`rem-rule${rule.enabled ? '' : ' is-off'}`}>
                <div className="rem-rule__main">
                  <span className="ui-icon-chip ui-icon-chip--md" data-tone={rule.enabled ? meta.tone : 'neutral'} aria-hidden="true">
                    <Icon />
                  </span>
                  <div className="rem-rule__text">
                    <h3 className="rem-rule__title">{title}</h3>
                    {known && <p className="rem-rule__desc">{t(`reminders.rules.items.${rule.key}.desc`)}</p>}
                    <div className="rem-rule__meta">
                      {known && <span>{t(`reminders.rules.items.${rule.key}.when`)}</span>}
                      <ChannelIcons channels={rule.channels} t={t} />
                    </div>
                  </div>
                </div>

                <dl className="rem-rule__stats" aria-label={t('reminders.rules.last30')}>
                  <div>
                    <dt>{t('reminders.rules.sent')}</dt>
                    <dd>{formatNumber(stats.sent || 0)}</dd>
                  </div>
                  <div>
                    <dt>{t('reminders.rules.opened')}</dt>
                    <dd>{formatNumber(stats.clicked || 0)}</dd>
                  </div>
                  <div>
                    <dt>{t(`reminders.rules.converted.${kind}`)}</dt>
                    <dd>{formatNumber(stats.converted || 0)}</dd>
                  </div>
                </dl>

                <div className="rem-rule__controls">
                  <label className="rem-rule__switch">
                    <span className="rem-rule__state">{t(rule.enabled ? 'reminders.rules.on' : 'reminders.rules.off')}</span>
                    <input
                      type="checkbox"
                      role="switch"
                      className="ui-switch hit-44"
                      checked={Boolean(rule.enabled)}
                      disabled={busy.has(rule.key)}
                      aria-label={title}
                      onChange={() => toggle(rule)}
                    />
                  </label>
                  <button
                    type="button"
                    className="ds-btn ds-btn-secondary ds-btn-sm"
                    aria-label={`${t('reminders.rules.edit')}: ${title}`}
                    onClick={() => setEditing(rule)}
                  >
                    <Pencil aria-hidden="true" />
                    {t('reminders.rules.edit')}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {editing && (
        <RuleEditModal
          rule={editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            if (saved) replace(saved);
            else setReloadKey((k) => k + 1);
            setEditing(null);
            toast.success(t('reminders.rules.saved'));
          }}
        />
      )}
    </div>
  );
}
